import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppBackButton } from '../components/AppBackButton';
import {
  ArrowLeft, Loader2, Sparkles, AlertTriangle,
  ChevronLeft, ChevronRight, Hash, ChevronsLeft, ChevronsRight
} from 'lucide-react';
import * as pdfjs from 'pdfjs-dist';
import { CustomCatalogBook } from './LibriTuiLibraryPage';
import { storageService } from '../services/storageService';
import { fileStorageService } from '../services/fileStorageService';
import { Book } from '../types';
import { useTheme } from '../contexts/ThemeContext';

import { useAuth } from '../contexts/AuthContext';
import { dbManager } from '../services/db_manager';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379/pdf.worker.min.mjs`;

interface PageAnalysis {
  maskHeight: number;
  isFullLicense: boolean;
}

const PreviewReader: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const bookData = location.state?.book as CustomCatalogBook;

  const [pdf, setPdf] = useState<pdfjs.PDFDocumentProxy | null>(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0); // Índice no availablePages
  const [loading, setLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom] = useState(1.4);

  const [availablePages, setAvailablePages] = useState<number[]>([]);
  const [pageAnalysisMap, setPageAnalysisMap] = useState<Record<number, PageAnalysis>>({});

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const renderTaskRef = useRef<any>(null);

  const MAX_PREVIEW_PAGES = 10;

  /**
   * Identificação Híbrida por Coordenadas (Sincronizada com ReaderPage)
   */
  const analyzePage = async (doc: pdfjs.PDFDocumentProxy, pageNum: number): Promise<PageAnalysis> => {
    try {
      const page = await doc.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1.0 });
      const content = await page.getTextContent();

      const licenseTokens = content.items.filter((item: any) =>
        item.str.includes("LibriTui Library") ||
        item.str.includes("EBook") ||
        item.str.includes("Produced by") ||
        item.str.includes("*** START OF")
      );

      if (licenseTokens.length > 0) {
        let maxLicenseY = 0;
        let foundStartMarker = false;

        licenseTokens.forEach((item: any) => {
          const tx = pdfjs.Util.transform(viewport.transform, item.transform);
          const yBottom = tx[5];

          if (item.str.includes("*** START OF")) {
            maxLicenseY = yBottom;
            foundStartMarker = true;
          } else if (!foundStartMarker && yBottom > maxLicenseY) {
            maxLicenseY = yBottom;
          }
        });

        // Margem de segurança de 60px (conforme revisado)
        const yThreshold = maxLicenseY + 60;

        const usefulTokensBelow = content.items.filter((item: any) => {
          const tx = pdfjs.Util.transform(viewport.transform, item.transform);
          return tx[5] > yThreshold && item.str.trim().length > 3;
        });

        const isFullLicense = usefulTokensBelow.length < 5;
        return { maskHeight: isFullLicense ? 0 : yThreshold, isFullLicense };
      }
    } catch (e) {
      console.error(`[Preview OCR] Erro na P${pageNum}:`, e);
    }
    return { maskHeight: 0, isFullLicense: false };
  };

  useEffect(() => {
    if (!bookData) {
      navigate('/libritui-library');
      return;
    }

    const loadPdf = async () => {
      try {
        setLoading(true);
        const response = await fetch(bookData.file);
        if (!response.ok) throw new Error("Erro ao baixar amostra.");

        const buffer = await response.arrayBuffer();
        const doc = await pdfjs.getDocument({ data: buffer }).promise;
        setPdf(doc);

        // Varredura Estendida: Primeiras 3 páginas
        const analysis: Record<number, PageAnalysis> = {};
        const available: number[] = [];
        const scanCount = Math.min(doc.numPages, 3);

        for (let i = 1; i <= doc.numPages; i++) {
          if (i <= scanCount) {
            const res = await analyzePage(doc, i);

            // ATIVAÇÃO OBRIGATÓRIA: Se for livro da pasta pdfs/ e a detecção falhou, aplica altura padrão de segurança
            if (res.maskHeight === 0 && bookData.file.toLowerCase().includes('/pdfs/') && !res.isFullLicense) {
              res.maskHeight = 160;
            }

            analysis[i] = res;
            if (!res.isFullLicense) available.push(i);
          } else {
            available.push(i);
          }
          if (available.length >= MAX_PREVIEW_PAGES) break;
        }

        setPageAnalysisMap(analysis);
        setAvailablePages(available);
        setLoading(false);

        // Scroll de Ancoragem se a primeira página disponível tiver máscara
        if (available.length > 0) {
          const firstPage = available[0];
          const firstAnalysis = analysis[firstPage];
          if (firstAnalysis && firstAnalysis.maskHeight > 0) {
            setTimeout(() => {
              if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = firstAnalysis.maskHeight * zoom;
              }
            }, 500);
          }
        }
      } catch (err: any) {
        console.error("Preview Load Error:", err);
        setError("Não foi possível carregar a amostra.");
        setLoading(false);
      }
    };

    loadPdf();
  }, [bookData, navigate]);

  const renderPage = async (pageNum: number) => {
    if (!pdf || !canvasRef.current) return;

    try {
      if (renderTaskRef.current) {
        await renderTaskRef.current.cancel();
      }

      const page = await pdf.getPage(pageNum);
      const dpr = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale: zoom * dpr });

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d', { alpha: false });

      if (context) {
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${Math.floor(viewport.width / dpr)}px`;
        canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;

        context.fillStyle = 'white';
        context.fillRect(0, 0, canvas.width, canvas.height);

        const renderTask = page.render({
          canvasContext: context,
          viewport: viewport,
          canvas: canvas
        });

        renderTaskRef.current = renderTask;
        await renderTask.promise;
      }
    } catch (err: any) {
      if (err.name !== 'RenderingCancelledException') {
        console.error("Preview Render Error:", err);
      }
    }
  };

  useEffect(() => {
    if (pdf && availablePages.length > 0) {
      renderPage(availablePages[currentPageIndex]);
    }
  }, [pdf, currentPageIndex, availablePages, zoom]);

  const handleImport = async () => {
    if (!bookData) return;
    setIsImporting(true);
    try {
      const response = await fetch(bookData.file);
      const downloadedBlob = await response.blob();
      const finalBlob = new Blob([downloadedBlob], { type: 'application/pdf' });

      const bookId = `gut-${bookData.id}-${Date.now()}`;
      await fileStorageService.saveFile(bookId, finalBlob);

      const newBook: Book = {
        id: bookId,
        title: bookData.title,
        author: bookData.author,
        coverUrl: bookData.cover || `generated://${bookId}`,
        fileUrl: `indexeddb://${bookId}`,
        lastPage: 1,
        totalPages: 0,
        dateAdded: Date.now(),
        category: `LibriTui Library (PDF)`,
        isFavorite: false,
        language: bookData.language
      };

      storageService.saveBook(newBook);
      localStorage.setItem(`mime_${bookId}`, 'application/pdf');

      // Sync to D1 if user is available (including Guests)
      if (user) {
        try {
          await dbManager.from('books', user.id).upsert({
            id: bookId,
            custom_id: bookId,
            title: bookData.title,
            author: bookData.author,
            cover_url: bookData.cover || `generated://${bookId}`,
            file_url: `indexeddb://${bookId}`,
            last_page: 1,
            user_id: user.id,
            file_type: 'pdf',
            language: bookData.language,
            category: `LibriTui Library (PDF)`
          });
        } catch (d1Err) {
          console.error("Failed to sync preview book to D1:", d1Err);
        }
      }

      navigate(`/reader/${bookId}`, { state: { importedBook: newBook } });
    } catch (err: any) {
      console.error("Import Error:", err);
      alert("Erro ao importar livro.");
    } finally {
      setIsImporting(false);
    }
  };

  const jumpToIndex = (idx: number) => {
    const target = Math.max(0, Math.min(idx, availablePages.length - 1));
    setCurrentPageIndex(target);
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;

    const pageNum = availablePages[target];
    const analysis = pageAnalysisMap[pageNum];
    if (analysis && analysis.maskHeight > 0) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = analysis.maskHeight * zoom;
        }
      }, 100);
    }
  };

  if (loading) return (
    <div className="h-screen flex flex-col items-center justify-center bg-transparent">
      <Loader2 className="animate-spin text-primary mb-4" size={48} />
      <p className="text-muted font-normal uppercase font-mono text-[10px] tracking-[0.14em]">{t('preview.loadingSample')}</p>
    </div>
  );

  if (error) return (
    <div className="h-screen flex flex-col items-center justify-center bg-ink text-surface p-10 text-center gap-6">
      <AlertTriangle className="text-bad" size={64} />
      <h2 className="text-2xl font-medium">{error}</h2>
      <button onClick={() => navigate('/libritui-library')} className="px-8 py-4 bg-primary text-slate-900 rounded-2xl font-medium text-[10px]">Voltar</button>
    </div>
  );

  const currentPageNum = availablePages[currentPageIndex];
  const currentAnalysis = pageAnalysisMap[currentPageNum];
  const totalDisplayPages = availablePages.length;

  return (
    <div className="fixed inset-0 flex flex-col h-full w-full overflow-hidden bg-tint-stone/60">
      <header className="h-20 bg-surface/90 backdrop-blur-md border-b border-black/[0.06] dark:border-white/[0.06] flex items-center justify-between px-6 z-[80] shrink-0">
        <div className="flex items-center gap-4">
          <AppBackButton fallbackTo="/libritui-library" />
          <div className="min-w-0">
            <h1 className="text-sm font-semibold truncate max-w-[200px] md:max-w-[400px]">{bookData.title}</h1>
            <p className="text-[9px] font-normal text-primary uppercase font-mono tracking-[0.14em]">{t('preview.previewMode')}</p>
          </div>
        </div>
      </header>

      <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[90] w-[92vw] max-w-2xl bg-white/60 backdrop-blur-xl border border-white/20 dark:border-white/[0.06] rounded-3xl p-4 md:p-5 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] flex flex-col sm:flex-row items-center justify-between gap-4 animate-in slide-in-from-top-10">
        <p className="text-[11px] md:text-sm font-bold dark:text-ink text-center sm:text-left flex items-center gap-2">
          <Sparkles className="text-primary shrink-0" size={18} />
          <span>{t('preview.saveToUnlock')}</span>
        </p>
        <button
          onClick={handleImport}
          disabled={isImporting}
          className="bg-primary text-slate-900 px-6 py-3 rounded-2xl font-medium text-[10px] hover:scale-[1.02] active:scale-95 transition-all whitespace-nowrap disabled:opacity-50"
        >
          {isImporting ? <Loader2 size={16} className="animate-spin" /> : `+ ${t('library.saveToShelf', 'Salvar na Estante')}`}
        </button>
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-auto p-4 md:p-10 custom-scrollbar flex justify-center items-start pt-48 pb-32">
        <div className="relative rounded-sm bg-surface overflow-hidden">
          {/* Máscara Dinâmica com Corte Sólido Branco no Preview */}
          {currentAnalysis && currentAnalysis.maskHeight > 0 && (
            <div
              className="absolute top-0 left-0 right-0 z-50 pointer-events-none"
              style={{
                height: currentAnalysis.maskHeight * zoom,
                backgroundColor: '#ffffff'
              }}
            />
          )}
          <canvas ref={canvasRef} className="block" />
        </div>
      </div>

      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-2 bg-surface/80 backdrop-blur-xl p-2 rounded-[24px] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] border border-white/20">
        <button onClick={() => jumpToIndex(0)} className="p-3 rounded-2xl hover:bg-tint-stone/60 transition-colors disabled:opacity-30 active:scale-90" disabled={currentPageIndex === 0}>
          <ChevronsLeft size={20} />
        </button>
        <button onClick={() => jumpToIndex(currentPageIndex - 1)} className="p-3 rounded-2xl hover:bg-tint-stone/60 transition-colors disabled:opacity-30 active:scale-90" disabled={currentPageIndex === 0}>
          <ChevronLeft size={20} />
        </button>
        <div className="px-6 py-2 bg-ink text-surface rounded-2xl font-medium text-[10px] flex items-center gap-2">
          <Hash size={12} />
          Pág {currentPageIndex + 1} / {totalDisplayPages}
        </div>
        <button onClick={() => jumpToIndex(currentPageIndex + 1)} className="p-3 rounded-2xl hover:bg-tint-stone/60 transition-colors disabled:opacity-30 active:scale-90" disabled={currentPageIndex === totalDisplayPages - 1}>
          <ChevronRight size={20} />
        </button>
        <button onClick={() => jumpToIndex(totalDisplayPages - 1)} className="p-3 rounded-2xl hover:bg-tint-stone/60 transition-colors disabled:opacity-30 active:scale-90" disabled={currentPageIndex === totalDisplayPages - 1}>
          <ChevronsRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default PreviewReader;